import { useQuery } from '@tanstack/react-query'

import { fetchCheckIfIdentifyingInformationExists } from '@app/domains/layers/utils/layers.utils'
import type { IdentifyingInformation } from '@app/domains/layers/types/layers.types'
import type { MapLayer } from '@app/types/layerTypes'

interface UseAvailableIdentifyingInformationProps {
  elementLayers: MapLayer[]
  potentialIdentifyingInformation: IdentifyingInformation[]
  isAuthenticated: boolean
  clientName: string | null
}

export function useAvailableIdentifyingInformation({
  elementLayers,
  potentialIdentifyingInformation,
  isAuthenticated,
  clientName,
}: UseAvailableIdentifyingInformationProps) {
  return useQuery({
    queryKey: [
      'risk',
      'available-identifying-information',
      clientName,
      elementLayers.map((layer) => layer.type),
      potentialIdentifyingInformation.map((info) => info.property),
    ],

    queryFn: async () => {
      const backendCheckedInfo = potentialIdentifyingInformation.filter(
        (info) => info.property !== 'damage_cost',
      )

      const damageCostInfo = potentialIdentifyingInformation.find(
        (info) => info.property === 'damage_cost',
      )

      const responses = await Promise.all(
        backendCheckedInfo.map((info) =>
          fetchCheckIfIdentifyingInformationExists(info.property, elementLayers),
        ),
      )

      const availableInformation = responses.flat()

      const byAssetType = availableInformation.reduce(
        (acc, info) => {
          const identifyingInformation = potentialIdentifyingInformation.find(
            (candidate) => candidate.property === info.attribute,
          )

          if (!identifyingInformation) {
            return acc
          }

          if (!acc[info.asset_type]) {
            acc[info.asset_type] = []
          }

          acc[info.asset_type].push(identifyingInformation)

          return acc
        },
        {} as Record<string, IdentifyingInformation[]>,
      )

      if (damageCostInfo) {
        for (const assetType of Object.keys(byAssetType)) {
          const hasReplacementCost = byAssetType[assetType].some(
            (info) => info.property === 'replacement_cost',
          )

          if (hasReplacementCost) {
            byAssetType[assetType].push(damageCostInfo)
          }
        }
      }

      return byAssetType
    },

    enabled: isAuthenticated && elementLayers.length > 0,
  })
}
